import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { unwrapList } from '../../utils/format.js'
import { useDebounce } from '../../hooks/useDebounce.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'

/**
 * Patient-facing OTC drug browser.
 *
 * Search the global formulary filtered to is_otc=True (server-enforced —
 * patients can never see prescription-only drugs even by manipulating
 * the URL). Pick a drug → expands to show every active pharmacy that
 * currently stocks it, with brand + price + stock_quantity.
 *
 * Helps a patient answer: "I need paracetamol — who has it cheapest?"
 */
export default function PatientOtcBrowser() {
  const [q, setQ] = useState('')
  const debounced = useDebounce(q, 250)
  const [openId, setOpenId] = useState(null)

  const drugsQ = useQuery({
    queryKey: ['otc-drugs', debounced],
    queryFn: async () => {
      const params = { is_otc: true }
      if (debounced.trim().length >= 2) params.search = debounced.trim()
      const { data } = await api.get(endpoints.drugSearch, { params })
      return unwrapList(data)
    },
    enabled: debounced.trim().length >= 2 || debounced === '',
    staleTime: 60_000,
  })
  const rows = drugsQ.data || []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Over-the-counter medicines"
          subtitle="Search common medicines you can buy directly from a pharmacy without a prescription. Pick a drug to see which pharmacies stock it and at what price."
          right={
            <Link to="/patient" className="text-xs text-wantok-700 underline">
              ← Back to my home
            </Link>
          }
        />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder='Search by name (e.g. "paracetamol", "loratadine", "ibuprofen")…'
          className="w-full border rounded px-3 py-2 text-sm"
        />
      </Card>

      {drugsQ.error && <ErrorBanner error={drugsQ.error} onRetry={() => drugsQ.refetch()} />}

      {drugsQ.isLoading && (
        <div className="text-sm text-slate-400 text-center py-6">Searching…</div>
      )}

      {!drugsQ.isLoading && debounced.trim().length === 0 && (
        <Card>
          <div className="text-sm text-slate-500">
            Start typing a medicine name above to see what's available without
            a prescription. For prescription medicines, your doctor sends
            them directly to a pharmacy on your behalf.
          </div>
        </Card>
      )}

      {!drugsQ.isLoading && debounced.trim().length > 0 && rows.length === 0 && (
        <Card>
          <div className="text-sm text-slate-500">
            No over-the-counter medicines match <b>"{debounced}"</b>. The
            medicine you're looking for may be prescription-only — speak
            to a doctor on the platform.
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((d) => (
          <DrugRow
            key={d.id}
            drug={d}
            expanded={openId === d.id}
            onToggle={() => setOpenId(openId === d.id ? null : d.id)}
          />
        ))}
      </div>
    </div>
  )
}

function DrugRow({ drug, expanded, onToggle }) {
  return (
    <Card>
      <button
        onClick={onToggle}
        className="w-full text-left flex items-center justify-between gap-3"
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">
            {drug.medication_name}
            <span className="ml-2 text-slate-500 font-normal text-xs">
              {drug.strength}
            </span>
            {drug.dosage_form && (
              <span className="ml-2 text-slate-400 italic text-xs">
                {drug.dosage_form}
              </span>
            )}
          </div>
          {drug.therapeutic_class && (
            <div className="text-[11px] text-slate-500 mt-0.5">
              {drug.therapeutic_class}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {drug.indicative_price_pgk != null && (
            <span className="text-[11px] text-slate-500">
              from PGK {drug.indicative_price_pgk}
            </span>
          )}
          <span className="text-wantok-700 text-sm">
            {expanded ? '▾' : '▸'}
          </span>
        </div>
      </button>
      {expanded && <DrugAvailability drugId={drug.id} />}
    </Card>
  )
}

function DrugAvailability({ drugId }) {
  const stockQ = useQuery({
    queryKey: ['otc-drug-stock', drugId],
    queryFn: async () => {
      const { data } = await api.get(`${endpoints.drugSearch}${drugId}/stock/`)
      return data
    },
    staleTime: 30_000,
  })
  const rows = stockQ.data || []

  if (stockQ.isLoading) {
    return <div className="mt-3 text-sm text-slate-400">Loading availability…</div>
  }
  if (stockQ.error) {
    return <div className="mt-3"><ErrorBanner error={stockQ.error} /></div>
  }
  if (rows.length === 0) {
    return (
      <div className="mt-3 text-sm text-slate-500 italic">
        No pharmacies have this in stock right now. Try a doctor consultation —
        they may suggest an alternative.
      </div>
    )
  }

  // Sort cheapest first (rows without price drop to the bottom).
  const sorted = [...rows].sort((a, b) => {
    const ap = a.price ?? Infinity
    const bp = b.price ?? Infinity
    return ap - bp
  })

  return (
    <div className="mt-3 border-t pt-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
        {sorted.length} pharmac{sorted.length === 1 ? 'y' : 'ies'} stocking this — cheapest first
      </div>
      <ul className="divide-y divide-slate-100">
        {sorted.map((row) => (
          <li key={row.id} className="py-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">
                {row.pharmacy_name}
              </div>
              <div className="text-[11px] text-slate-500 flex flex-wrap gap-2 mt-0.5">
                {row.brand_name && <span>Brand: {row.brand_name}</span>}
                {row.stock_quantity > 0 ? (
                  <span className="text-emerald-700">{row.stock_quantity} in stock</span>
                ) : (
                  <span className="text-amber-700">stock not specified</span>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-semibold text-slate-800">
                {row.price != null ? `PGK ${row.price}` : '—'}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
